import React from 'react';
import { Folder, LogOut, AlertCircle } from 'lucide-react';
import { useFileSystem } from '../hooks/useFileSystem';
import { useFileSystemStore } from '../store/useFileSystemStore';

export const WorkspaceConnector: React.FC = () => {
    const { isConnected, folderName, lastError, connect, disconnect } = useFileSystem();
    // US-12-07 follow-up: skeleton during init window (handle lookup + permission query)
    // to avoid flashing "Kết nối Thư mục" button before connect state settles.
    const isInitializing = useFileSystemStore((s) => s.isInitializing);

    if (isInitializing) {
        // Match the connected-state layout so transition is smooth (no layout shift).
        return (
            <div className="flex flex-col gap-2 p-2 bg-indigo-50/30 rounded-xl border border-indigo-100/40">
                <div className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="w-5 h-5 rounded bg-gradient-to-br from-indigo-100 to-indigo-200 shrink-0 animate-pulse" />
                    <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="h-3 w-3/4 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded animate-pulse" />
                        <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-pulse"></span>
                            <div className="h-2 w-12 bg-slate-100 rounded animate-pulse" />
                        </div>
                    </div>
                </div>
                <div className="flex gap-1">
                    <div className="flex-1 h-6 rounded-lg bg-slate-100/60 animate-pulse" />
                    <div className="w-8 h-6 rounded-lg bg-slate-100/60 animate-pulse" />
                </div>
            </div>
        );
    }

    if (isConnected) {
        return (
            <div className="flex flex-col gap-2 p-2 bg-indigo-50/50 rounded-xl border border-indigo-100/50">
                <div className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                    <Folder className="w-5 h-5 text-indigo-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate" title={folderName}>
                            {folderName}
                        </p>
                        <p className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            Bộ nhớ Cục bộ
                        </p>
                    </div>
                </div>
                <button
                    onClick={disconnect}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
                    title="Ngắt kết nối thư mục"
                >
                    <LogOut className="w-3 h-3" />
                    Ngắt kết nối
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <button
                onClick={connect}
                className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-indigo-50 text-slate-600 hover:text-indigo-700 rounded-xl border border-slate-200 hover:border-indigo-200 shadow-sm transition-all group active:scale-[0.98]"
            >
                <div className="w-8 h-8 bg-slate-100 group-hover:bg-indigo-100 rounded-lg flex items-center justify-center transition-colors">
                    <Folder className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </div>
                <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">Kết nối Thư mục</p>
                    <p className="text-[10px] text-slate-400 group-hover:text-indigo-400 truncate">Lưu trữ dữ liệu dự án</p>
                </div>
            </button>

            {lastError && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg border border-red-100">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <p className="text-[10px] font-medium leading-tight">{lastError}</p>
                </div>
            )}
        </div>
    );
};
